import { Component, ElementRef, EventEmitter, Input, Output, QueryList, TemplateRef, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { SelectOption } from '../../models/shared.models';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {isMobile} from "@app/group/helpers/is-mobile";
import { first } from 'rxjs/operators';


/**
 * A select like input with customized styling
 * matching the rest of the UI
 * That will look the same independent of the browser
 *
 * @Input options your options with a value and a label
 * @Input defaultOption the first selected option
 * @Output emits the value of the selected option
 * The name of the input is "search-select" so browser plugins like
 * 1Pass don't try to autocomplete the input
 */
@Component({
    selector: 'it-select',
    templateUrl: './it-select.component.html',
    styleUrls: ['./it-select.component.scss'],
    standalone: false
})
export class ItSelectComponent {
  @Input() error: string;
  @Input() label: string;
  @Input() editable = false;
  @Input() placeholder: string;
  @Input() set options(options: Array<SelectOption>) {
    this.filteredOptions = options;
    this.allOptions = [...options];
  }
  @Input() set initialOption(option: SelectOption) {
    this._selectedOption = option;
    this.inputText = option?.label;
  }
  @Input() upward = false;
  @Input() disabled = false;
  @Output() changeOption = new EventEmitter<SelectOption>();
  @Output() blurInput = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild('dropdown') dropdown: TemplateRef<any>;
  @ViewChildren('dropdownElement') dropdownElements: QueryList<ElementRef>;
  dropdownToggle = false;
  set selectedOption(option: SelectOption) {
    this._selectedOption = option;
    this.changeOption.emit(option);
  }
  get selectedOption(): SelectOption {
    return this._selectedOption;
  }
  inputText: string;
  filteredOptions: Array<SelectOption>;
  invalid = false;
  isMobile$ = isMobile();
  private isPointerDown = false;
  private _selectedOption: SelectOption;
  private allOptions: Array<SelectOption>;
  private overlayRef: OverlayRef;
  // Scroll strategy is unreliable due to overlay being deeply nested from the scrollable container
  private scrollListener: () => void;

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) {}

  // Close overlay on scroll to avoid odd behaviour
  ngOnInit(): void {
    document.addEventListener('scroll', this.scrollListener, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.scrollListener, true);
    this.overlayRef?.dispose();
  }

  onPointerDown() {
    this.isPointerDown = true;
  }

  onFocus() {
    if(this.isPointerDown){
      return;
    }

    this.openDropdown();
    this.isPointerDown = false;
  }

  /**
   * Expand dropdown
   */
  openDropdown() {
    if(this.dropdownToggle) {
      this.isMobile$.pipe(first()).subscribe(isMobile => isMobile && this.closeDropdown());

      return;
    }
    this.inputText = '';
    this.dropdownToggle = true;
    this.createOverlay();
    if(this.editable) {
      this.onNgModelChange(this.inputText);
    }
  }

  /**
   * Close the select dropdown
   */
  closeDropdown() {
    this.inputText = this.selectedOption?.label;
    this.dropdownToggle = false;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  /**
   * Validate whether the label is a valid option label
   * @returns true only if input text is either null or a whole label of one option
   */
  isValidateOptionLabel() {
    return !this.inputText || this.allOptions.find(option => option.label === this.inputText);
  }

  /**
   * Toggle dropdown
   */
   toggleDropdown() {
    if(this.dropdownToggle) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  /**
   * Set selected option, close dropdown, and reset invalid status of input text
   * @param option an option clicked
   */
  optionClick(option: SelectOption) {
    this.selectedOption = option;
    this.closeDropdown();
    this.invalid = false;
    this.changeOption.emit(option);
  }

  /**
   * When a key is pressed control the list with the keyboard
   * @param event the key event emmited by the browser
   */
  onKeyDown(event: any) {
    const index = this.filteredOptions.findIndex((x) => this.selectedOption === x) ?? 0;
    switch (event.key) {
      case 'ArrowDown':
      case 'Tab':
        if(!this.dropdownToggle) {
          return;
        }
        event.preventDefault();
        const nextIndex = (index + 1) % this.filteredOptions.length;
        this.selectedOption = this.filteredOptions[nextIndex];
        this.dropdownElements.toArray()[nextIndex].nativeElement.scrollIntoView({ block: 'end' });
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (index > 0) {
          const prevIndex = (index - 1) % this.filteredOptions.length;
          this.selectedOption = this.filteredOptions[prevIndex];
          this.dropdownElements.toArray()[prevIndex].nativeElement.scrollIntoView({ block: 'end' });
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedOption) {
          this.closeDropdown();
        }
        break;
      case 'Backspace':
        if( this.inputText === '' && this.selectedOption && this.editable) {
          this.selectedOption = null;
        }
    }
  }

  /**
   * When input text changes filter the options
   * @param event the string with the new text from the input
   */
  onNgModelChange(event: string) {
    const normalizedEvent = event.toLocaleLowerCase();
    this.filteredOptions = this.allOptions.filter((element) => {
      const normalizedElementLabel = element.label.toLocaleLowerCase();
      const normalizedElementValue = element.value.toLocaleLowerCase();
      return normalizedElementValue.includes(normalizedEvent) || normalizedElementLabel.includes(normalizedEvent);
    });

    if (this.inputText !== '') {
      this.selectedOption = this.filteredOptions[0] ?? this.allOptions[0];
    }

    if (this.isValidateOptionLabel()){
      this.invalid = false;
    }
  }

  onBlur() {
    this.blurInput.emit();
  }

  onClickOutside() {
    this.closeDropdown();
    if(this.searchInput.nativeElement === document.activeElement) {
      this.searchInput.nativeElement.blur();
    }
  }

  resetSelectedOption(){
    this._selectedOption = null;
    this.inputText = null;
    this.changeOption.emit(null);
  }

  private createOverlay() {
    if(this.overlayRef) {
      return;
    }
    const config = new OverlayConfig({
      positionStrategy: this.overlay.position()
      .flexibleConnectedTo(this.searchInput)
      .withFlexibleDimensions(true)
      .withPush(false)
      .withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -8
      }]),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      minWidth: this.searchInput.nativeElement.offsetWidth,
      maxWidth: this.searchInput.nativeElement.offsetWidth
    });
   this.overlayRef = this.overlay.create(config);
   const template = new TemplatePortal(this.dropdown, this.viewContainerRef);
   this.overlayRef.attach(template);
  }
}
